import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Grid,
  Avatar,
  Chip,
  CircularProgress,
  IconButton,
  alpha,
  Divider,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  Tooltip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Star as StarIcon,
  Block as BlockIcon,
  Queue as QueueIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import useUsuarios from '../../hooks/useUsuarios';
import { formatarCargo } from '../../utils/formatters';
import api from '../../services/api';
import { toast } from 'react-toastify';

const AtendenteCard = ({ usuario, selecionado, onClick, disabled, senhaAtual }) => {
  // Status de cores para diferentes estatutos de atendentes
  const getStatusColor = (usuario) => {
    if (!usuario.ativo) return '#9e9e9e'; // Cinza para inativos
    if (disabled || senhaAtual) return '#f44336'; // Vermelho para ocupados
    return '#4caf50'; // Verde para disponíveis
  };

  // Determinar o status do atendente (Disponível, Ocupado, Inativo)
  const getStatusText = (usuario) => {
    if (!usuario.ativo) return 'Inativo';
    if (disabled || senhaAtual) return 'Ocupado';
    return 'Disponível';
  };

  const card = (
    <Paper 
      elevation={selecionado ? 4 : 1}
      onClick={() => !disabled && onClick(usuario)}
      sx={{
        p: 2,
        cursor: disabled ? 'not-allowed' : 'pointer',
        borderRadius: 2,
        border: '1px solid',
        borderColor: selecionado ? 'primary.main' : 'divider',
        backgroundColor: disabled 
          ? alpha('#f44336', 0.1) 
          : selecionado 
            ? alpha('#2196f3', 0.05) 
            : 'background.paper',
        transition: 'all 0.2s ease',
        position: 'relative',
        overflow: 'hidden',
        opacity: disabled ? 0.6 : 1,
        '&:hover': !disabled ? {
          borderColor: selecionado ? 'primary.main' : alpha('#2196f3', 0.5),
          transform: 'translateY(-2px)',
          boxShadow: 3
        } : {},
        '&::after': selecionado ? {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          width: 0,
          height: 0,
          borderStyle: 'solid',
          borderWidth: '0 24px 24px 0',
          borderColor: 'transparent #2196f3 transparent transparent',
          transform: 'translate(0, 0)'
        } : {}
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {disabled && (
          <BlockIcon 
            sx={{ 
              position: 'absolute',
              top: 8,
              right: 8,
              color: '#f44336',
              fontSize: 20
            }} 
          />
        )}
        
        {!disabled && (
        <Radio 
          checked={selecionado}
          sx={{ 
            position: 'absolute',
            top: 8,
            right: 8,
            opacity: selecionado ? 1 : 0,
            transition: 'opacity 0.2s ease',
            '.MuiRadio-root': {
              p: 0
            }
          }} 
        />
        )}
        
        <Avatar 
          sx={{ 
            width: 48, 
            height: 48, 
            bgcolor: alpha(getStatusColor(usuario), 0.2),
            color: getStatusColor(usuario),
            border: `2px solid ${getStatusColor(usuario)}`,
            mr: 2
          }}
        >
          {usuario.nome ? usuario.nome.charAt(0).toUpperCase() : <PersonIcon />}
        </Avatar>
        
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight="medium">
            {usuario.nome}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
            {usuario.cargo && (
              <Chip 
                label={formatarCargo(usuario.cargo)} 
                size="small" 
                color="info"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
            
            <Chip 
              label={getStatusText(usuario)} 
              size="small" 
              sx={{ 
                height: 20, 
                fontSize: '0.7rem',
                bgcolor: alpha(getStatusColor(usuario), 0.1),
                color: getStatusColor(usuario),
                borderColor: getStatusColor(usuario)
              }}
              variant="outlined"
            />
            
            {usuario.guiche && (
              <Chip 
                label={`Guichê ${usuario.guiche}`} 
                size="small" 
                variant="outlined"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Box>
          
          {/* Mostrar informação da senha atual se ocupado */}
          {senhaAtual && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="error" sx={{ fontWeight: 'medium' }}>
                Atendendo: Senha {senhaAtual.numero}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );

  // Se estiver desabilitado, envolver com tooltip explicativo
  if (disabled) {
    return (
      <Tooltip 
        title={
          senhaAtual 
            ? `Este atendente está ocupado atendendo a senha ${senhaAtual.numero}` 
            : "Este atendente não está disponível no momento"
        }
        placement="top"
      >
        <Box>{card}</Box>
      </Tooltip>
    );
  }

  return card;
};

const EnviarParaAtendenteModal = ({ 
  open, 
  onClose, 
  onConfirm, 
  senha, 
  mostrarOpcaoVoltarFila = false, 
  onVoltarParaFila = null,
  tituloPersonalizado = "Enviar Senha para Atendente"
}) => {
  const { usuarios, carregando, erro, carregarUsuarios } = useUsuarios();
  const [searchTerm, setSearchTerm] = useState('');
  const [atendenteSelecionado, setAtendenteSelecionado] = useState(null);
  const [modalCarregado, setModalCarregado] = useState(false);
  const [senhasAtendentes, setSenhasAtendentes] = useState({});
  const [carregandoStatus, setCarregandoStatus] = useState(false);

  // Função para buscar o status atual dos atendentes
  const buscarStatusAtendentes = async (usuariosParaVerificar) => {
    if (!usuariosParaVerificar || usuariosParaVerificar.length === 0) {
      //console.log('🔍 Modal: Nenhum usuário para verificar');
      return;
    }

    try {
      //console.log('🔍 Modal: Buscando status individual de cada atendente...');
      
      // Filtrar apenas atendentes/admins ativos E com ID válido
      const atendentesAtivos = usuariosParaVerificar.filter(u => 
        u.ativo && 
        ['ATENDENTE', 'ADMIN'].includes(u.cargo) &&
        u.id !== undefined &&
        u.id !== null &&
        u.id !== ''
      );

      //console.log('👥 Modal: Verificando status de:', atendentesAtivos.map(u => ({ id: u.id, nome: u.nome })));

      if (atendentesAtivos.length === 0) {
        //console.warn('⚠️ Modal: Nenhum atendente ativo com ID válido encontrado');
        setSenhasAtendentes({});
        return;
      }

      // Verificar status individual de cada atendente
      const statusPromises = atendentesAtivos.map(async (atendente) => {
        try {
          // ✅ VALIDAÇÃO CRÍTICA: Verificar se o ID do atendente é válido antes da consulta
          if (!atendente.id || atendente.id === undefined || atendente.id === null) {
            //console.warn(`⚠️ Modal: Atendente ${atendente.nome} tem ID inválido:`, atendente.id);
            return {
              atendenteId: atendente.id,
              senhaAtual: null
            };
          }

          //console.log(`🔍 Modal: Consultando status do atendente ${atendente.nome} (ID: ${atendente.id})`);
          const response = await api.get(`/senhas/atendente/${atendente.id}/em-atendimento`);
          
          if (response.data.success && response.data.data) {
            //console.log(`👤 Modal: Atendente ${atendente.nome} (ID: ${atendente.id}) está ocupado com senha ${response.data.data.numero}`);
            return {
              atendenteId: atendente.id,
              senhaAtual: {
                numero: response.data.data.numero,
                status: response.data.data.status,
                tipo: response.data.data.tipo
              }
            };
          } else {
            //console.log(`✅ Modal: Atendente ${atendente.nome} (ID: ${atendente.id}) está disponível`);
            return {
              atendenteId: atendente.id,
              senhaAtual: null
            };
          }
        } catch (error) {
          // Se retornou 404 ou erro similar, significa que não tem senha em atendimento (disponível)
          if (error.response?.status === 404 || error.response?.data?.message?.includes('Nenhuma senha em atendimento')) {
            //console.log(`✅ Modal
            return {
              atendenteId: atendente.id,
              senhaAtual: null
            };
          } else {
            //console.error(`❌ Modal: Erro ao verificar status do atendente ${atendente.nome}:`, error);
            // Em caso de erro, assumir como disponível para não bloquear desnecessariamente
            return {
              atendenteId: atendente.id,
              senhaAtual: null
            };
          }
        }
      });

      // Aguardar todas as verificações
      const resultados = await Promise.all(statusPromises);

      // Criar mapa de atendente -> senha atual
      const statusMap = {};
      resultados.forEach(resultado => {
        if (resultado.senhaAtual && resultado.atendenteId) {
          statusMap[resultado.atendenteId] = resultado.senhaAtual;
        }
      });

      //console.log('🗂️ Modal: Mapa final de atendentes ocupados (verificação individual):', statusMap);
      setSenhasAtendentes(statusMap);
    } catch (error) {
      //console.error('❌ Modal: Erro ao buscar status dos atendentes:', error);
      // Em caso de erro geral, limpar o mapa para não bloquear transferências
      setSenhasAtendentes({});
    }
  };

  // Carregar dados apenas quando o modal abrir
  useEffect(() => {
    if (open) {
      //console.log('🚪 Modal: Abrindo modal - iniciando carregamento único');
      
      const carregarDadosCompletos = async () => {
        try {
          setCarregandoStatus(true);
          
          // 1. Carregar usuários
          //console.log('📋 Modal: Carregando usuários...');
          await carregarUsuarios();
          
          // 2. Buscar os usuários diretamente da API para garantir dados atualizados
          const responseUsuarios = await api.get('/usuarios');
          const usuariosAtualizados = responseUsuarios.data?.data || [];
          
          //console.log('👥 Modal: Usuários carregados:', usuariosAtualizados.length);
          
          // 3. Verificar status dos atendentes
          await buscarStatusAtendentes(usuariosAtualizados);
          
          setModalCarregado(true);
        } catch (error) {
          //console.error('❌ Modal: Erro ao carregar dados:', error);
          setModalCarregado(true);
        } finally {
          setCarregandoStatus(false);
        }
      };

      carregarDadosCompletos();
    }
    
    // Limpar estado quando o modal fechar
    if (!open) {
      setAtendenteSelecionado(null);
      setSearchTerm('');
      setModalCarregado(false);
      setSenhasAtendentes({});
    }
  }, [open]); // Apenas 'open' como dependência para evitar loops

  // Filtrar usuários: apenas ativos e atendentes
  const usuariosDisponiveis = usuarios
    .filter(usuario => 
      // Apenas usuários ativos e com cargo de atendente ou admin
      usuario.ativo && 
      ['ATENDENTE', 'ADMIN'].includes(usuario.cargo) &&
      // Filtrar pelo termo de busca
      (searchTerm === '' || 
       usuario.nome.toLowerCase().includes(searchTerm.toLowerCase()) || 
       usuario.usuario.toLowerCase().includes(searchTerm.toLowerCase()))
    );

  // Separar atendentes disponíveis e ocupados
  const atendentesOrdenados = usuariosDisponiveis.sort((a, b) => {
    const aOcupado = senhasAtendentes[a.id];
    const bOcupado = senhasAtendentes[b.id];
    
    // Disponíveis primeiro, depois ocupados
    if (!aOcupado && bOcupado) return -1;
    if (aOcupado && !bOcupado) return 1;
    
    // Dentro do mesmo grupo, ordenar por nome
    return a.nome.localeCompare(b.nome);
  });

  // Manipuladores de eventos
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleAtendenteClick = (usuario) => {
    //console.group('👆 MODAL: Clique no atendente');
    //console.log('👤 Usuário clicado:', usuario);
    //console.log('🆔 usuario.id:', usuario?.id);
    //console.log('📛 usuario.nome:', usuario?.nome);
    //console.log('🔒 Está ocupado?', senhasAtendentes[usuario.id]);
    //console.groupEnd();

    // Não permitir selecionar atendentes ocupados
    if (senhasAtendentes[usuario.id]) {
      //console.warn('⚠️ MODAL: Tentativa de selecionar atendente ocupado');
      return;
    }
    
    //console.log('✅ MODAL: Selecionando atendente:', usuario);
    setAtendenteSelecionado(usuario);
  };

  const handleConfirm = () => {
    //console.group('🔍 MODAL: Confirmando transferência');
    //console.log('📋 Senha:', senha);
    //console.log('👤 Atendente selecionado:', atendenteSelecionado);
    //console.log('🆔 senha.id:', senha?.id);
    //console.log('🆔 atendenteSelecionado.id:', atendenteSelecionado?.id);
    //console.groupEnd();

    if (atendenteSelecionado) {
      // Verificação final antes de confirmar
      if (senhasAtendentes[atendenteSelecionado.id]) {
        toast.error('Este atendente está ocupado. Por favor, selecione outro atendente.');
        return;
      }
      
      console.log('📤 MODAL: Chamando onConfirm com:', {
        senhaId: senha.id,
        atendente: atendenteSelecionado
      });
      
      onConfirm(senha.id, atendenteSelecionado);
      handleClose(); // Fechar modal após confirmação
    } else {
      //console.error('❌ MODAL: Nenhum atendente selecionado');
      toast.error('Por favor, selecione um atendente antes de confirmar.');
    }
  };

  const handleClose = () => {
    onClose();
  };

  const atendentesDisponiveis = atendentesOrdenados.filter(u => !senhasAtendentes[u.id]);
  const atendentesOcupados = atendentesOrdenados.filter(u => senhasAtendentes[u.id]);

  // Debug logs para verificar a separação
  //console.log('👥 Modal: Atendentes ordenados:', atendentesOrdenados.map(u => ({ id: u.id, nome: u.nome })));
  //console.log('✅ Modal: Atendentes disponíveis:', atendentesDisponiveis.map(u => ({ id: u.id, nome: u.nome })));
  //console.log('🚫 Modal: Atendentes ocupados:', atendentesOcupados.map(u => ({ id: u.id, nome: u.nome, senha: senhasAtendentes[u.id]?.numero })));

  return (
    <Dialog 
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden'
        }
      }}
    >
      {/* Cabeçalho do modal */}
      <DialogTitle sx={{ 
        p: 2,
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        bgcolor: 'primary.dark',
        color: 'white'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h6" component="div">
            {tituloPersonalizado}
          </Typography>
          {senha && (
            <Chip 
              label={senha.senha || senha.numero} 
              color="warning"
              sx={{ ml: 2, fontWeight: 'bold' }}
            />
          )}
        </Box>
        <IconButton
          edge="end"
          color="inherit"
          onClick={handleClose}
          aria-label="fechar"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 3 }}>
        {/* Detalhes da senha */}
        {senha && (
          <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: alpha('#f5f5f5', 0.5), borderRadius: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Detalhes da Senha
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Número</Typography>
                <Typography variant="body1" fontWeight="medium">{senha.senha || senha.numero}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Serviço</Typography>
                <Typography variant="body1" fontWeight="medium">{senha.servico_nome || senha.tipo}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Tipo</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {senha.preferencial ? (
                    <Chip 
                      icon={<StarIcon fontSize="small" />} 
                      label="Preferencial" 
                      size="small" 
                      color="warning"
                    />
                  ) : (
                    <Chip label="Normal" size="small" color="primary" />
                  )}
                </Box>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Alerta sobre verificação de status */}
        {carregandoStatus && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Verificando status atual dos atendentes...
          </Alert>
        )}

        {/* Campo de pesquisa */}
        <TextField
          fullWidth
          placeholder="Pesquisar atendente..."
          variant="outlined"
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            sx: {
              borderRadius: 2
            }
          }}
          sx={{ mb: 3 }}
        />

        {/* Lista de atendentes */}
        {carregando ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : erro ? (
          <Typography color="error" align="center">{erro}</Typography>
        ) : atendentesOrdenados.length === 0 ? (
          <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
            Nenhum atendente encontrado
          </Typography>
        ) : atendentesDisponiveis.length === 0 && atendentesOcupados.length > 0 ? (
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Todos os atendentes estão ocupados no momento. Aguarde ou tente novamente em alguns instantes.
            </Alert>
            
            {/* Mostrar atendentes ocupados mesmo quando não há disponíveis */}
            <Box>
              <Typography variant="h6" color="error.main" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                Atendentes Ocupados ({atendentesOcupados.length})
              </Typography>
              <Grid container spacing={2}>
                {atendentesOcupados.map((usuario) => (
                  <Grid item xs={12} sm={6} md={4} key={usuario.id}>
                    <AtendenteCard 
                      usuario={usuario} 
                      selecionado={false}
                      onClick={() => {}}
                      disabled={true}
                      senhaAtual={senhasAtendentes[usuario.id]}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        ) : (
          <Box>
            {/* Atendentes Disponíveis */}
            {atendentesDisponiveis.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" color="success.main" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  Atendentes Disponíveis ({atendentesDisponiveis.length})
                </Typography>
          <Grid container spacing={2}>
                  {atendentesDisponiveis.map((usuario) => (
              <Grid item xs={12} sm={6} md={4} key={usuario.id}>
                <AtendenteCard 
                  usuario={usuario} 
                  selecionado={atendenteSelecionado?.id === usuario.id}
                  onClick={handleAtendenteClick}
                        disabled={false}
                        senhaAtual={null}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Atendentes Ocupados */}
            {atendentesOcupados.length > 0 ? (
              <Box>
                <Typography variant="h6" color="error.main" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  Atendentes Ocupados ({atendentesOcupados.length})
                </Typography>
                <Grid container spacing={2}>
                  {atendentesOcupados.map((usuario) => (
                    <Grid item xs={12} sm={6} md={4} key={usuario.id}>
                      <AtendenteCard 
                        usuario={usuario} 
                        selecionado={false}
                        onClick={() => {}}
                        disabled={true}
                        senhaAtual={senhasAtendentes[usuario.id]}
                />
              </Grid>
            ))}
          </Grid>
              </Box>
            ) : (
              <Box sx={{ mt: 2, p: 3, bgcolor: alpha('#4caf50', 0.1), borderRadius: 2, border: '1px dashed', borderColor: 'success.main' }}>
                <Typography variant="body2" color="success.dark" align="center" sx={{ fontWeight: 'medium' }}>
                  🎉 Todos os atendentes estão disponíveis no momento!
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Divisor entre opções */}
        {mostrarOpcaoVoltarFila && (
          <Box sx={{ display: 'flex', alignItems: 'center', my: 3 }}>
            <Divider sx={{ flex: 1 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mx: 2 }}>
              ou
            </Typography>
            <Divider sx={{ flex: 1 }} />
          </Box>
        )}

        {/* Opção de voltar para fila (se habilitada) */}
        {mostrarOpcaoVoltarFila && onVoltarParaFila && (
          <Paper 
            elevation={2} 
            sx={{ 
              p: 3, 
              mb: 3, 
              borderRadius: 2,
              border: '2px solid',
              borderColor: 'primary.main',
              bgcolor: alpha('#2196F3', 0.05),
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: alpha('#2196F3', 0.1),
                boxShadow: 3
              }
            }}
            onClick={() => {
              onVoltarParaFila();
              handleClose();
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <QueueIcon sx={{ color: 'primary.main', mr: 2, fontSize: 28 }} />
              <Box>
                <Typography variant="h6" color="primary.main" sx={{ fontWeight: 600 }}>
                  Retornar para a Fila
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Clique aqui para devolver a senha ao início da fila de espera
                </Typography>
              </Box>
            </Box>
            <Typography variant="body2" color="primary.dark" sx={{ fontStyle: 'italic' }}>
              Esta opção colocará a senha como próxima a ser chamada
            </Typography>
          </Paper>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button 
          onClick={handleClose}
          variant="outlined"
          color="inherit"
          sx={{ borderRadius: 2 }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={!atendenteSelecionado || carregandoStatus}
          sx={{ 
            borderRadius: 2,
            px: 3,
            boxShadow: 2,
            background: 'linear-gradient(45deg, #2196F3 30%, #00BCD4 90%)'
          }}
        >
          {carregandoStatus ? 'Verificando...' : 'Confirmar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EnviarParaAtendenteModal; 